import React, { useEffect, useRef } from "react";
import { Message, Persona, ScenarioDocument } from "../types";
import { downloadFile } from "../utils/fileUtils";
import { selectAutoplayMessage } from "../utils/autoplaySelection";
import MarkdownMessage from "./MarkdownMessage";
import PersonaAvatar from "./PersonaAvatar";
import { useSpeechInput } from "../hooks/useSpeechInput";
import { useTextToSpeech } from "../hooks/useTextToSpeech";

/**
 * The one measure the transcript and the composer both use.
 *
 * They must stay identical: the bug this replaces was a composer centred at
 * 896px beneath messages pinned left at 768px and right at 672px, so nothing
 * shared an axis and the gap grew with the screen. clamp keeps the column
 * near-full-width on a laptop and readable rather than stranded on a large
 * display.
 */
const THREAD_COLUMN = "w-full mx-auto max-w-[clamp(0px,100%,62rem)] px-6";

// A prose measure rather than a share of the column: `ch` is font-relative, so
// a bubble holds roughly the same number of characters per line whatever the
// viewport does. The percentage caps on the rows outside this stay as the
// binding constraint on narrow screens, where 66ch is wider than the column.
// `min-w-0` is what lets the cap apply at all — a flex child defaults to
// min-width:auto, which a long unbroken token can push past a max-width.
const BUBBLE_MEASURE = "min-w-0 max-w-[66ch]";

interface ChatWindowProps {
  persona: Persona;
  messages: Message[];
  documents: ScenarioDocument[];
  isLoading: boolean;
  inputValue: string;
  setInputValue: (val: string) => void;
  onSendMessage: () => void;
  onExportChat: () => void;
  getToken: () => string | undefined;
  hue: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  persona,
  messages,
  documents,
  isLoading,
  inputValue,
  setInputValue,
  onSendMessage,
  onExportChat,
  getToken,
  hue,
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const {
    isSupported: micSupported,
    isListening,
    error: speechError,
    toggle: toggleDictation,
  } = useSpeechInput({ value: inputValue, onChange: setInputValue });
  const {
    ttsEnabled,
    autoplay,
    toggleAutoplay,
    playingId,
    loadingId: speechLoadingId,
    errorId: speechErrorId,
    toggle: toggleSpeech,
  } = useTextToSpeech(getToken);

  // Message ids this window has already spoken or deliberately skipped, reset
  // per persona. Autoplay must speak genuinely new replies and nothing else —
  // opening a session, switching persona, or reloading must never replay a
  // conversation out loud.
  const spokenRef = useRef<{ personaId: string | null; seen: Set<string> }>({
    personaId: null,
    seen: new Set(),
  });

  useEffect(() => {
    const tracker = spokenRef.current;
    if (tracker.personaId !== persona.id) {
      tracker.personaId = persona.id;
      tracker.seen = new Set();
    }

    const unseen = messages.filter((m) => !tracker.seen.has(m.id));
    messages.forEach((m) => tracker.seen.add(m.id));

    if (!autoplay) return;

    const latest = selectAutoplayMessage(unseen);
    if (latest) void toggleSpeech(latest.id, latest.text, persona.id);
  }, [messages, autoplay, persona.id, toggleSpeech]);

  // Scroll only the message list to its bottom. We deliberately avoid
  // scrollIntoView: it scrolls every scrollable ancestor (including the
  // overflow-hidden chat/layout wrappers), which could yank the whole pane —
  // input bar included — to the top of the viewport.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, [messages, isLoading]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  const getDocument = (docId: string) => documents.find((d) => d.id === docId);

  const formatTime = (ts: number) => {
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-surface relative overflow-hidden">
      {/* Messages Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto py-8">
        {/* flex column + min-h-full is load-bearing, not decoration: the empty
            state centres itself with justify-center, which needs a parent that
            actually fills the scroll area. A percentage height would NOT work
            here — min-height alone does not make this box definite-height, so
            a child h-full would collapse. */}
        <div className={`${THREAD_COLUMN} flex flex-col min-h-full space-y-10`}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center flex-1 text-slate-500 opacity-60">
            <PersonaAvatar
              name={persona.name}
              src={persona.avatar}
              className="w-16 h-16 rounded border border-outline-variant/20 mb-4"
              imgClassName="grayscale brightness-75"
              textClassName="text-xl"
            />
            <p className="text-center text-sm font-label uppercase tracking-widest">
              Begin your session with
            </p>
            <p className="font-headline font-bold text-lg text-on-surface mt-1">{persona.name}</p>
          </div>
        )}

        {messages.map((msg) => {
          const isUser = msg.role === "user";
          const revealedDoc = msg.revealedDocId ? getDocument(msg.revealedDocId) : null;

          if (isUser) {
            return (
              <div key={msg.id} className="flex gap-4 max-w-[72%] ml-auto flex-row-reverse group">
                <div className="flex-shrink-0 w-10 h-10 rounded bg-secondary-container flex items-center justify-center border border-outline-variant/20">
                  <span className="material-symbols-outlined text-secondary">person</span>
                </div>
                <div className={`space-y-2 text-right ${BUBBLE_MEASURE}`}>
                  <div className="flex items-center gap-3 justify-end">
                    <span className="text-[10px] text-slate-500 font-label">{formatTime(msg.timestamp)}</span>
                    <span className="text-xs font-label font-bold text-secondary tracking-widest uppercase">You</span>
                  </div>
                  <div className="bg-surface-container-high text-on-surface p-4 rounded-lg font-body leading-relaxed text-base text-left">
                    <MarkdownMessage text={msg.text} />
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={msg.id} className="flex gap-4 max-w-[84%] group">
              <PersonaAvatar
                name={persona.name}
                src={persona.avatar}
                className="w-10 h-10 rounded border border-outline-variant/20"
                imgClassName="grayscale brightness-90"
              />
              <div className={`space-y-2 ${BUBBLE_MEASURE}`}>
                <div className="flex items-center gap-3">
                  <span
                    className="text-xs font-label font-bold tracking-widest uppercase"
                    style={{ color: hue }}
                  >
                    {persona.name}
                  </span>
                  <span className="text-[10px] text-slate-500 font-label">{formatTime(msg.timestamp)}</span>
                  {ttsEnabled && !msg.isError && (
                    <button
                      onClick={() => toggleSpeech(msg.id, msg.text, persona.id)}
                      disabled={speechLoadingId === msg.id}
                      title={
                        speechErrorId === msg.id
                          ? "Could not generate audio. Click to retry."
                          : playingId === msg.id
                            ? "Stop"
                            : "Play aloud"
                      }
                      aria-label={playingId === msg.id ? "Stop audio" : "Play message aloud"}
                      className={`flex items-center justify-center h-6 w-6 rounded transition-colors ${
                        speechErrorId === msg.id
                          ? "text-error"
                          : "text-slate-500 hover:text-primary hover:bg-surface-container-high"
                      }`}
                    >
                      <span
                        className={`material-symbols-outlined text-sm ${
                          speechLoadingId === msg.id ? "animate-spin" : ""
                        }`}
                      >
                        {speechLoadingId === msg.id
                          ? "progress_activity"
                          : playingId === msg.id
                            ? "stop_circle"
                            : "volume_up"}
                      </span>
                    </button>
                  )}
                </div>
                <div
                  className={`bg-surface-container border-l-2 p-4 rounded-lg font-body leading-relaxed text-base ${
                    msg.isError ? "text-error" : "text-on-surface"
                  }`}
                  style={{ borderLeftColor: hue }}
                >
                  <MarkdownMessage text={msg.text} />
                </div>

                {revealedDoc && (
                  <div className="mt-2 p-3 bg-surface-container rounded-lg border border-primary/20 flex items-center gap-3">
                    <div className="bg-primary-container p-2 rounded">
                      <span className="material-symbols-outlined text-primary text-base">description</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-primary font-label font-bold uppercase tracking-widest">
                        File Revealed
                      </div>
                      <div className="text-sm text-on-surface font-medium truncate">{revealedDoc.name}</div>
                    </div>
                    <button
                      onClick={() => downloadFile(revealedDoc)}
                      className="p-2 hover:bg-surface-container-high rounded text-primary transition-colors"
                      title="Open File"
                    >
                      <span className="material-symbols-outlined text-base">download</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex gap-4 max-w-[84%] animate-pulse">
            <div className="flex-shrink-0 w-10 h-10 rounded bg-surface-container-highest/50 flex items-center justify-center overflow-hidden">
              <span className="material-symbols-outlined text-slate-600">generating_tokens</span>
            </div>
            <div className="flex items-center gap-1 mt-4">
              <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <div className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <div className="w-1.5 h-1.5 bg-primary/30 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Input Area */}
      <div className="py-6 bg-surface border-t border-outline-variant/10">
        <div className={THREAD_COLUMN}>
          <div className="flex items-center gap-3 bg-surface-bright p-2 rounded-md border border-outline-variant/20 focus-within:border-primary/40 transition-all duration-300">
            <textarea
              rows={1}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Message ${persona.name}...`}
              disabled={isLoading}
              className="flex-1 bg-transparent border-none text-on-surface font-body text-base resize-none py-1 placeholder:text-slate-500 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="flex gap-2 py-1">
              {micSupported && (
                <button
                  onClick={toggleDictation}
                  disabled={isLoading}
                  title={isListening ? "Stop dictation" : "Dictate message"}
                  aria-label={isListening ? "Stop dictation" : "Dictate message"}
                  className={`h-10 w-10 flex items-center justify-center rounded transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                    isListening
                      ? "bg-error/20 text-error animate-pulse"
                      : "text-slate-400 hover:text-on-surface hover:bg-surface-container-high"
                  }`}
                >
                  <span className="material-symbols-outlined text-base">
                    {isListening ? "stop_circle" : "mic"}
                  </span>
                </button>
              )}
              <button
                onClick={onSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="bg-primary text-on-primary h-10 w-10 flex items-center justify-center rounded hover:scale-105 active:scale-95 transition-all shadow-[0_0_10px_rgba(60,221,199,0.3)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <span className="material-symbols-outlined text-base">send</span>
              </button>
            </div>
          </div>
          {speechError && (
            <p className="mt-2 text-center text-xs font-label text-error">{speechError}</p>
          )}
          <div className="flex justify-center mt-3 gap-6">
            {ttsEnabled && (
              <button
                onClick={toggleAutoplay}
                title={
                  autoplay
                    ? "Stop speaking new replies automatically"
                    : "Speak new replies automatically"
                }
                aria-pressed={autoplay}
                className={`flex items-center gap-1.5 text-[10px] font-label uppercase tracking-widest transition-colors ${
                  autoplay
                    ? "text-primary"
                    : "text-slate-500 hover:text-on-surface"
                }`}
              >
                <span className="material-symbols-outlined text-sm">
                  {autoplay ? "volume_up" : "volume_off"}
                </span>
                <span>Autoplay</span>
              </button>
            )}
            {messages.length > 0 && (
              <button
                onClick={onExportChat}
                className="flex items-center gap-1.5 text-[10px] font-label text-slate-500 hover:text-on-surface uppercase tracking-widest transition-colors"
                title="Export Chat"
              >
                <span className="material-symbols-outlined text-sm">download</span>
                <span>Export Chat</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
